/**
 * @file events.api.js
 * @module EventsAPI
 * @description HTTP API wrapper for event-related endpoints. Methods return Axios promises.
 */

import { axiosInstance } from "./axios";

export const EventsAPI = {
    /**
     * Get paginated events.
     * @param {number} [limit=15] - Page size.
     * @param {number} [page=1] - Page number.
     * @param {string} [query=""] - Extra query string (search, status, customer, from, to, sortBy, sortType).
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to `{ count, page, pages, docs }`.
     */
    getEvents: (limit = 15, page = 1, query = "", token = "") => {
        return axiosInstance(token).get(`/events?limit=${limit}&page=${page}${query ? `&${query}` : ""}`);
    },

    /**
     * Create an event.
     * @param {Object} event - `name`, `code`, `description`, `startDate`, `endDate`, `timezone`, `status`, `customer`.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to the created event.
     */
    createEvent: (event, token = "") => {
        return axiosInstance(token).post(`/events`, event);
    },

    /**
     * Update an event. Only the fields sent are changed.
     * @param {string} eventId - The event ID.
     * @param {Object} event - The fields to update.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to the updated event.
     */
    updateEventById: (eventId, event, token = "") => {
        return axiosInstance(token).put(`/events/${eventId}`, event);
    },

    /**
     * Get active events that have not ended yet, for select inputs.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to options list.
     */
    getEventOptions: (token = "") => {
        return axiosInstance(token).get(`/event-options`);
    },

    /**
     * Get an event by ID.
     * @param {string} eventId - The event ID.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to the event.
     */
    getEventById: (eventId, token = "") => {
        return axiosInstance(token).get(`/events/${eventId}`);
    }
};
